import React, { useState, useContext } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretDown, faCaretUp, faStar, faEye } from '@fortawesome/free-solid-svg-icons'
import axios from 'axios'
import Swal from 'sweetalert2'
import AuthContext from '../../../middlewares/AuthContext'
import { useTranslation } from 'react-i18next'

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface Plan {
  id         : number
  name       : string
  description: string
  expiry_time: number
  best_value : boolean
  Time       : { name: string }
}

interface Product {
  id         : number
  name       : string
  description: string
  image      : string
  Plans      : Plan[]
}

interface Props {
  product    : Product
  i          : number
  url        : string | undefined
  setProducts: (p: Product[]) => void
}

const pill = (bg: string): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
  padding: '0.25rem 0.6rem', borderRadius: 9999, backgroundColor: bg,
  color: '#fff', fontWeight: 700, fontSize: '0.75rem', border: 'none',
  cursor: 'pointer',
})

const CardProduct: React.FC<Props> = ({ product: e, i, url, setProducts }) => {
  const { getHeaders } = useContext(AuthContext) as AuthCtx
  const { t }          = useTranslation()

  const [plansOpen, setPlansOpen] = useState(false)

  const restoreProduct = async (id: number) => {
    const result = await Swal.fire({
      title: t('products.restore_product'),
      text: t('products.restore_product_txt'),
      showCancelButton: true,
      confirmButtonText: t('products.restore'),
      customClass: { confirmButton: 'btn btn-outline-danger', cancelButton: 'btn btn-primary mx-2' },
      buttonsStyling: false,
    })
    if (!result.isConfirmed) return
    try {
      const headers = await getHeaders()
      const res     = await axios.put(`${url}products/restore/${id}`, {}, { headers })
      setProducts(res.data.message)
      Swal.fire({ icon: 'success', title: res.data.response })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  return (
    <div className="row w-100 mt-2">
      <div className="col-12">
        <div style={{ backgroundColor: '#fff', borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid #f1f5f9', overflow: 'hidden', marginBottom: '1rem' }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND_DARK}, ${BRAND})` }} />
          <div className="row g-0">

            {/* Image */}
            <div className="col-md-3 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8fafc', minHeight: 140 }}>
              <img
                src={`${url}images/${e.image}`}
                className="img-fluid p-3"
                alt={e.name}
                style={{ maxHeight: 140, objectFit: 'contain' }}
              />
            </div>

            {/* Content */}
            <div className="col-md-9">
              <div style={{ padding: '1rem 1.25rem' }}>

                {/* Header row */}
                <div className="d-flex align-items-start justify-content-between mb-2">
                  <div>
                    <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>{e.name}</p>
                    <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b' }}>{e.description}</p>
                  </div>
                  <button style={pill('#0ea5e9')} onClick={() => restoreProduct(e.id)} title={t('products.restore')}>
                    <FontAwesomeIcon icon={faEye} />
                  </button>
                </div>

                {/* Plans toggle */}
                <button
                  type="button"
                  onClick={() => setPlansOpen(p => !p)}
                  style={{ ...pill(plansOpen ? BRAND_DARK : BRAND), width: '100%', justifyContent: 'center', padding: '0.4rem 1rem', fontSize: '0.82rem' }}
                >
                  <span>{t('products.plans_title')}</span>
                  <FontAwesomeIcon icon={plansOpen ? faCaretUp : faCaretDown} />
                </button>

                {/* Plans panel */}
                {plansOpen && (
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0.75rem 0 0' }}>
                    {e.Plans.map((el, il) => (
                      <li key={il} style={{ borderBottom: '1px solid #f1f5f9', padding: '0.6rem 0' }}>
                        <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                          <p className="mb-0"><b>{t('products.plan_name')}:</b> {el.name}</p>
                          <p className="mb-0"><b>{t('products.plan_desc')}:</b> {el.description}</p>
                          <p className="mb-0 text-capitalize"><b>{t('products.duration')}:</b> {el.expiry_time} {el.Time.name}</p>
                          {el.best_value
                            ? <p className="mb-0"><b>{t('products.plans.best')}: </b><FontAwesomeIcon icon={faStar} style={{ color: 'gold' }} /></p>
                            : <p className="mb-0 text-muted">{t('products.plans.normal')}</p>
                          }
                        </div>
                      </li>
                    ))}
                    {e.Plans.length === 0 && (
                      <li style={{ fontSize: '0.78rem', color: '#94a3b8', padding: '0.5rem 0' }}>—</li>
                    )}
                  </ul>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CardProduct
